import React, { useCallback, useMemo } from 'react';

import { Menu, MenuButton, MenuList, MenuItem, Flex } from '@chakra-ui/react';
// import { StyledMenuItem } from 'uiToolkitV1/Options/Options.style';
import styled from '@emotion/styled';
import { logoutLoggedInEmployee } from 'actions/loggedInEmployee';
import NotificationsPanel from 'components/Notifications/NotificationsPanel';
import { useNotifications } from 'components/Notifications/context';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { ArrowDown, ArrowUp, Help, Logout, Settings } from 'assets/assetsV1/iconComponents';

import { getProfileRoute } from 'constants/helper';
import {
  sendClickedViewProfileEvent,
  sendHelpCenterClickedEvent,
  sendLogoutEvent,
  sendNotificationPanelOpenedEvent,
} from 'constants/mixpanelHelper';
import { routes } from 'constants/routes';

import { getPrimaryPod } from 'services/helpers/team';

import { Avatar } from 'uiToolkitV1/Avatar';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

const StyledMenuList = styled(MenuList)`
  &:focus:not([data-focus-visible-added]) {
    box-shadow:
      2px 0px 4px 3px rgba(0, 0, 0, 0.12),
      0px 4px 4px 3px rgba(0, 0, 0, 0.08);
    border-radius: 8px;
  }
  padding: 8px;
  border-radius: 8px;
`;

const StyledMenuItem = styled(MenuItem)`
  border-radius: 4px;
  &:active {
    background: #e6f1fd;
  }
  &:focus {
    background: #e6f1fd;
  }
  &:hover {
    background: #e6f1fd;
    p {
      color: #333333;
    }
    svg {
      color: #333333;
    }
  }
`;

const TruncatedTextMedium = styled(TextBodyMedium)`
  display: -webkit-box;
  max-width: 200px;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ProfileCard = () => {
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);

  return (
    <Flex maxW="320px" alignItems="center" height="60px">
      <Avatar size={24} src={loggedInEmployee?.profilePhotoSrc} />
      <Flex ml="12px" flexDir="column">
        <TextBodyMedium
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '150px',
          }}
          bold
        >
          {loggedInEmployee.fullName}
        </TextBodyMedium>
        {loggedInEmployee?.designation && (
          <TruncatedTextMedium color="text.g2">{loggedInEmployee.designation}</TruncatedTextMedium>
        )}
      </Flex>
    </Flex>
  );
};

export const MenuListContent = ({ icon, displayName, className = '' }) => {
  const IconComp = icon;
  return (
    <Flex alignItems="center" className={className} h={checkNewNavEnabled() ? '' : '36px'}>
      <IconComp color="text.g2" mr={checkNewNavEnabled() ? '0.5rem' : '12px'} boxSize="24px" />
      <TextBodyMedium>{displayName}</TextBodyMedium>
    </Flex>
  );
};

const UserSettingsDropdownRabbit = () => {
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  const {
    state: { notificationsCount },
  } = useNotifications();
  const dispatch = useDispatch();
  const history = useHistory();

  const logout = useCallback(() => {
    dispatch(logoutLoggedInEmployee());
  }, [dispatch]);

  const userMenuItems = useMemo(() => {
    const menuItems = [
      {
        id: 'AVATAR',
        content: () => <ProfileCard employee={loggedInEmployee} />,
        onClick() {
          sendClickedViewProfileEvent({
            eventProperties: {
              empId: loggedInEmployee.id,
              source: 'My Profile',
              teamId: getPrimaryPod(loggedInEmployee.teams)?.id ?? null,
              profileType: 'User',
            },
          });
        },
        href: getProfileRoute(loggedInEmployee.id),
        padding: '8px',
      },

      {
        id: 'NOTIFICATIONS',
        content: ({ onClose }) => <NotificationsPanel closeMenu={onClose} />,
        onClick: () => {
          sendNotificationPanelOpenedEvent();
        },
        padding: '8px',
        closeMenuOnClick: false,
      },
      {
        id: 'SETTINGS',
        content: () => <MenuListContent icon={Settings} displayName="Settings" />,
        onClick() {
          //   history.push(routes.settings.myProfile);
        },
        href: routes.settings.myProfile,
        className: '',
        padding: '8px',
      },

      {
        id: 'HELP_CENTER',
        content: () => <MenuListContent icon={Help} displayName="Help Center" />,
        onClick() {
          //   history.push(routes.settings.knowledgeHub);
          sendHelpCenterClickedEvent();
        },
        href: routes.settings.knowledgeHub,
        padding: '8px',
      },
      {
        id: 'LOGOUT',
        content: () => <MenuListContent icon={Logout} displayName="Logout" />,
        onClick: () => {
          sendLogoutEvent();
          logout();
        },
        padding: '8px',
      },
    ];

    return menuItems;
  }, [loggedInEmployee, logout, history]);

  return (
    <Menu closeOnSelect={false}>
      {({ isOpen, onClose }) => (
        <>
          <MenuButton>
            <Flex className="onboardingUserSettings" p={1} cursor="pointer">
              <Flex style={{ alignItems: 'center' }}>
                <Avatar
                  name={loggedInEmployee?.displayName}
                  src={loggedInEmployee.profilePhotoSrc}
                  notificationCount={notificationsCount}
                  size={32}
                />
                {isOpen ? <ArrowUp boxSize="24px" /> : <ArrowDown boxSize="24px" />}
              </Flex>
            </Flex>
          </MenuButton>
          <StyledMenuList>
            {userMenuItems?.map(({ id, content, onClick, padding, href, closeMenuOnClick = true }, index) => {
              if (!href) {
                return (
                  <StyledMenuItem
                    onClick={() => {
                      onClick();
                      if (closeMenuOnClick) {
                        onClose();
                      }
                    }}
                    p={padding}
                  >
                    {content({ onClose })}
                  </StyledMenuItem>
                );
              }
              return (
                <StyledMenuItem
                  onClick={() => {
                    onClick();
                    if (closeMenuOnClick) {
                      onClose();
                    }
                  }}
                  as={Link}
                  to={href}
                  p={padding}
                >
                  {content({ onClose })}
                </StyledMenuItem>
              );
            })}
          </StyledMenuList>
        </>
      )}
    </Menu>
  );
};

export default UserSettingsDropdownRabbit;
